import { WORK_ORDER_FORM } from "../lib/site";
import { VISA_CATALOG } from "./visaCatalog";

const APP_FORM = { name: "Visa Application Form", url: WORK_ORDER_FORM };

// China-specific forms & requirements
const CHINA_APP_URL = "https://consular.mfa.gov.cn/VISA/";
const CHINA_STATEMENT_FORM = { name: "Visa Statement Form", url: "https://customer-assets-jt897jd0.emergentagent.net/job_urgent-travel-docs/artifacts/hamxve74_china-visa-statement.pdf" };

const CHINA_TOURIST_REQS = [
  { title: "Completed Visa Application Form", desc: "Complete the official Chinese visa application online.", form: { name: "Chinese Visa Application (COVA)", url: CHINA_APP_URL } },
  { title: "Passport Photo", desc: "One 2×2 inch color photo on a plain light or white background." },
  { title: "Current Valid U.S. Passport", desc: "Ship your actual U.S. passport (no photocopies); valid at least 6 months beyond travel." },
  { title: "Copy of Driver's License", desc: "A clear color copy, front and back, of your current, valid U.S. driver's license or state ID." },
  { title: "Visa Statement Form", desc: "Please print, and sign the attached form.", form: CHINA_STATEMENT_FORM },
  { title: "Payment of Applicable Fees" },
];
const CHINA_BUSINESS_REQS = [
  { title: "Completed Visa Application Form", desc: "Complete the official Chinese visa application online.", form: { name: "Chinese Visa Application (COVA)", url: CHINA_APP_URL } },
  { title: "Passport Photo", desc: "One 2×2 inch color photo on a plain light or white background." },
  { title: "Current Valid U.S. Passport", desc: "Ship your actual U.S. passport (no photocopies); valid at least 6 months beyond travel." },
  { title: "Copy of Driver's License", desc: "A clear color copy, front and back, of your current, valid U.S. driver's license or state ID." },
  { title: "Letter of Invitation", desc: "From the company in China, with official stamp." },
  { title: "Visa Statement Form", desc: "Please print, and sign the attached form.", form: CHINA_STATEMENT_FORM },
  { title: "Payment of Applicable Fees" },
];

const withTotal = (arr) => arr.map((o) => ({ ...o, total: o.embassy + o.upv }));
const TIERED_FEES = withTotal([
  { label: "3 Months Single Entry", processing: "10 Business Days", embassy: 65, upv: 250 },
  { label: "1 Year Multiple Entry", processing: "10 Business Days", embassy: 105, upv: 250 },
  { label: "3 Months Single Entry (Rush)", processing: "2 Business Days", embassy: 105, upv: 250 },
  { label: "1 Year Multiple Entry (Rush)", processing: "2 Business Days", embassy: 205, upv: 250 },
]);

const DEFAULT_TOURIST_REQS = () => [
  { title: "Completed Visa Application Form", desc: "Fill out and sign the visa application form in full.", form: APP_FORM },
  { title: "Passport Photo", desc: "One 2×2 inch color photo on a plain light or white background." },
  { title: "Current Valid U.S. Passport", desc: "Ship your actual U.S. passport (no photocopies); valid at least 6 months beyond travel." },
  { title: "Copy of Driver's License", desc: "A clear photocopy of your current, valid U.S. driver's license or state ID." },
  { title: "Accommodation Information", desc: 'Complete the "Where You Stay" section on the application form.' },
  { title: "Payment of Applicable Fees" },
];
const DEFAULT_BUSINESS_REQS = (name) => [
  { title: "Completed Visa Application Form", desc: "Print, sign, and fill out completely.", form: APP_FORM },
  { title: "Passport Photo", desc: "One 2×2 inch color photo on a plain light or white background." },
  { title: "Current Valid U.S. Passport" },
  { title: "Copy of Driver's License", desc: "A clear photocopy of your current, valid U.S. driver's license or state ID." },
  { title: "Letter of Invitation", desc: `From the company in ${name}, with official stamp.` },
  { title: "Payment of Applicable Fees" },
];

export const SHIPPING_OPTIONS = [
  { id: "none", label: "No return label needed", cost: 0 },
  { id: "own", label: "Include your own label", cost: 0 },
  { id: "pickup", label: "Pick up from DC office", cost: 0 },
  { id: "fedex_wd", label: "FedEx overnight (weekdays)", cost: 45 },
  { id: "fedex", label: "FedEx overnight", cost: 65 },
];

export const DEFAULT_NOTES = [
  "Processing time for both visa types is typically 5–7 business days.",
  "Same-day processing is not available for business visas.",
  "Additional charges may apply for out-of-DC jurisdiction.",
  "Shipping fees are not included and should be arranged separately.",
];

// Keep only fee rows that have a real total (drops blanks like Russia).
const validFees = (rows) => (rows || []).filter((r) => typeof r.total === "number");

// Requirements & notes keep our own format. ONLY the fees are matched per
// country from the UPV store catalog; countries not in the catalog use defaults.
export const getCountryVisa = (slug, name) => {
  const c = VISA_CATALOG[slug];
  const tFees = validFees(c?.tourist?.fees);
  const bFees = validFees(c?.business?.fees);
  const isChina = slug === "china";
  const noTime = (rows) => (isChina ? rows.map((r) => ({ ...r, processing: "" })) : rows);
  return {
    tourist: { requirements: isChina ? CHINA_TOURIST_REQS : DEFAULT_TOURIST_REQS(name), fees: noTime(tFees.length ? tFees : TIERED_FEES) },
    business: { requirements: isChina ? CHINA_BUSINESS_REQS : DEFAULT_BUSINESS_REQS(name), fees: noTime(bFees.length ? bFees : TIERED_FEES) },
    notes: isChina ? DEFAULT_NOTES.filter((n) => !n.startsWith("Processing time")) : DEFAULT_NOTES,
  };
};

// Build the fee dropdown options (Tourist + Business) for the order form.
export const feeOptionLabel = (o) => `${o.category}: ${o.label}${o.processing ? ` — ${o.processing}` : ""} · $${o.embassy} Embassy + $${o.upv} UPV = $${o.total} Total`;

export const buildFeeOptions = (visa) => [
  ...visa.tourist.fees.map((f, i) => ({ id: `t${i}`, category: "Tourist", ...f })),
  ...visa.business.fees.map((f, i) => ({ id: `b${i}`, category: "Business", ...f })),
];

// ISO 3166-1 alpha-2 codes (for flagcdn.com) keyed by country name.
export const COUNTRY_ISO = {
  India: "in", China: "cn", Brazil: "br", Russia: "ru", Vietnam: "vn", Nigeria: "ng",
  "Saudi Arabia": "sa", "United Arab Emirates": "ae", Egypt: "eg", Kenya: "ke", Ghana: "gh",
  Turkey: "tr", Indonesia: "id", Cambodia: "kh", Myanmar: "mm", Tanzania: "tz", Uganda: "ug",
  Ethiopia: "et", Angola: "ao", Cameroon: "cm", "Ivory Coast": "ci", "DR Congo": "cd",
  Bahrain: "bh", Qatar: "qa", Kuwait: "kw", Oman: "om", Jordan: "jo", Lebanon: "lb",
  Pakistan: "pk", Bangladesh: "bd", "Sri Lanka": "lk", Nepal: "np", Australia: "au", Chad: "td",
  Mali: "ml", Senegal: "sn", Zambia: "zm", Zimbabwe: "zw", Mozambique: "mz", Gabon: "ga",
  Benin: "bj", "Burkina Faso": "bf", "Sierra Leone": "sl", Liberia: "lr", Rwanda: "rw",
  Sudan: "sd", Algeria: "dz", Morocco: "ma", Tunisia: "tn", Libya: "ly", Argentina: "ar",
  Bolivia: "bo", Suriname: "sr", Kazakhstan: "kz", Uzbekistan: "uz", Azerbaijan: "az",
  Mongolia: "mn", Laos: "la", Bhutan: "bt", Togo: "tg", Niger: "ne", Guinea: "gn",
  Gambia: "gm", Madagascar: "mg", Malawi: "mw", Namibia: "na", Djibouti: "dj", Eritrea: "er",
  Yemen: "ye", Iraq: "iq", Syria: "sy", Turkmenistan: "tm", Tajikistan: "tj", Kyrgyzstan: "kg",
  "Papua New Guinea": "pg", "Equatorial Guinea": "gq", "Central African Republic": "cf", Somalia: "so",
};

export const flagUrl = (name, w = 80) => {
  const code = COUNTRY_ISO[name];
  return code ? `https://flagcdn.com/w${w}/${code}.png` : null;
};
